/**
 * Reading a plan back to a person (F-144): one line per op for the card's
 * list, and a before → after preview of the cells it writes, so what will
 * land is reviewable BEFORE it lands. Pure over `BoardDoc` reads.
 */
import {
  isAgentTempColumnId,
  type AgentOp,
  type AgentPlan,
  type BoardDoc,
} from "@kola/shared";

export interface PreviewRow {
  /** "Row 12" for an existing row, "New row" for one the plan inserts. */
  row: string;
  column: string;
  before: string;
  after: string;
}

export interface PlanPreview {
  rows: PreviewRow[];
  /** Cell writes beyond `rows` (the preview is capped). */
  more: number;
}

export const PREVIEW_MAX_ROWS = 40;

/** Column names: the sheet's, plus the ones the plan creates (by temp id). */
export function columnNamer(board: BoardDoc, plan: AgentPlan): (colId: string) => string {
  const temp = new Map<string, string>();
  for (const op of plan.ops) if (op.op === "add_column") temp.set(op.colId, op.name);
  return (colId) => {
    if (isAgentTempColumnId(colId)) return temp.get(colId) ?? colId;
    return board.getColumn(plan.sheetId, colId)?.name ?? "(deleted column)";
  };
}

/** One plain sentence per op. */
export function describeOps(board: BoardDoc, plan: AgentPlan): string[] {
  const name = columnNamer(board, plan);
  const quote = (s: string) => `"${s.length === 0 ? "(untitled)" : s}"`;
  const lines: string[] = [];
  for (const op of plan.ops) {
    switch (op.op) {
      case "set_cells": {
        const byCol = new Map<string, { n: number; clears: number }>();
        for (const cell of op.cells) {
          const entry = byCol.get(cell.colId) ?? { n: 0, clears: 0 };
          entry.n++;
          if (cell.value === "") entry.clears++;
          byCol.set(cell.colId, entry);
        }
        for (const [colId, { n, clears }] of byCol) {
          const verb = clears === n ? "Clear" : "Write";
          lines.push(`${verb} ${n} ${n === 1 ? "cell" : "cells"} in ${quote(name(colId))}`);
        }
        break;
      }
      case "insert_rows": {
        const where = op.index === undefined ? "at the end" : `before row ${op.index + 1}`;
        lines.push(`Insert ${op.rows.length} ${op.rows.length === 1 ? "row" : "rows"} ${where}`);
        break;
      }
      case "delete_rows":
        lines.push(`Delete ${op.rowIds.length} ${op.rowIds.length === 1 ? "row" : "rows"}`);
        break;
      case "add_column": {
        const type = op.type !== undefined && op.type !== "text" ? ` (${op.type})` : "";
        const where = op.index === undefined ? "" : ` at position ${op.index + 1}`;
        lines.push(`Add column ${quote(op.name)}${type}${where}`);
        break;
      }
      case "rename_column":
        lines.push(`Rename ${quote(name(op.colId))} to ${quote(op.name)}`);
        break;
      case "delete_column":
        lines.push(`Delete column ${quote(name(op.colId))}`);
        break;
    }
  }
  return lines;
}

/** The first `max` cell writes as before → after, with the row's number. */
export function previewPlan(board: BoardDoc, plan: AgentPlan, max = PREVIEW_MAX_ROWS): PlanPreview {
  const name = columnNamer(board, plan);
  const rowNumber = new Map<string, number>();
  board.sortedRowIds(plan.sheetId).forEach((id, i) => rowNumber.set(id, i + 1));
  const rows: PreviewRow[] = [];
  let more = 0;
  const push = (row: PreviewRow) => {
    if (rows.length < max) rows.push(row);
    else more++;
  };
  for (const op of plan.ops) {
    if (op.op === "set_cells") {
      for (const cell of op.cells) {
        const n = rowNumber.get(cell.rowId);
        push({
          row: n === undefined ? "Row ?" : `Row ${n}`,
          column: name(cell.colId),
          before: isAgentTempColumnId(cell.colId) ? "" : board.getCellText(plan.sheetId, cell.rowId, cell.colId),
          after: cell.value,
        });
      }
    } else if (op.op === "insert_rows") {
      for (const row of op.rows) {
        for (const [colId, value] of Object.entries(row.cells)) {
          push({ row: "New row", column: name(colId), before: "", after: value });
        }
      }
    }
  }
  return { rows, more };
}

/** Does the plan remove anything? The card warns before a destructive apply. */
export function planDeletes(ops: readonly AgentOp[]): boolean {
  return ops.some((op) => op.op === "delete_rows" || op.op === "delete_column");
}
